import React, { useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Card,
  CardContent,
  Button,
  Avatar,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Preloader from "./components/Preloader";
import { handleLogout } from "./utils/utils";
import { getOrganizationSettings } from "./utils/storage";

export default function Settings({ setSidebarOpen }) {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch { }

  if (loading)
    return <Preloader fullScreen={true} message="Updating your settings..." />;

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleShow = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (form.new_password !== form.confirm_password) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const newOrg = getOrganizationSettings();
      const appid = newOrg.id;

      await axios.post(
        "http://localhost:8000/apichange-password",
        {
          current_password: form.current_password,
          new_password: form.new_password,
        },
        {
          headers: {
            Accept: "application/json",
            AppID: appid,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess("Password changed successfully!");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => setSidebarOpen && setSidebarOpen(true)}
              className="block xl:hidden text-gray-600 hover:text-gray-900 p-1"
            >
              <MenuIcon />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                Settings
              </h1>
              <p className="text-sm text-gray-600 truncate">
                Manage your account settings
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600 transition p-1 flex-shrink-0"
          >
            <LogoutIcon />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white border border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <Typography variant="h6" className="text-gray-900 mb-4">
                Change Password
              </Typography>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.current ? "text" : "password"}
                      name="current_password"
                      value={form.current_password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <IconButton
                      onClick={() => handleShow("current")}
                      aria-label={
                        show.current ? "Hide password" : "Show password"
                      }
                      size="small"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {show.current ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.new ? "text" : "password"}
                      name="new_password"
                      value={form.new_password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <IconButton
                      onClick={() => handleShow("new")}
                      aria-label={show.new ? "Hide password" : "Show password"}
                      size="small"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {show.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.confirm ? "text" : "password"}
                      name="confirm_password"
                      value={form.confirm_password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <IconButton
                      onClick={() => handleShow("confirm")}
                      aria-label={
                        show.confirm ? "Hide password" : "Show password"
                      }
                      size="small"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {show.confirm ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  color="primary"
                  fullWidth
                  className="mb-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-md"
                >
                  {loading ? "Changing..." : "Change Password"}
                </Button>
                {error && (
                  <Typography
                    color="error"
                    className="text-center text-red-600"
                  >
                    {error}
                  </Typography>
                )}
                {success && (
                  <Typography
                    color="success.main"
                    className="text-center text-green-600"
                  >
                    {success}
                  </Typography>
                )}
              </form>

              <div className="mt-8 space-y-6">
                <div>
                  <Typography variant="h6" className="text-gray-900 mb-2">
                    Security
                  </Typography>
                  <Typography className="text-gray-600">
                    Coming soon: Change PIN, 2FA, etc.
                  </Typography>
                </div>
                <div>
                  <Typography variant="h6" className="text-gray-900 mb-2">
                    Notifications
                  </Typography>
                  <Typography className="text-gray-600">
                    Coming soon: Email/SMS preferences.
                  </Typography>
                </div>
                <div>
                  <Typography variant="h6" className="text-gray-900 mb-2">
                    Preferences
                  </Typography>
                  <Typography className="text-gray-600">
                    Coming soon: Theme, language, etc.
                  </Typography>
                </div>
              </div>

              <Button
                onClick={handleLogout}
                variant="contained"
                color="error"
                fullWidth
                className="mt-8 bg-red-600 hover:bg-red-700 text-white font-medium py-3 rounded-md"
              >
                Logout
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
